import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
  isRouteErrorResponse,
} from "@remix-run/react";
import { json } from "@remix-run/node";
import * as Sentry from "@sentry/remix";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";

import "./tailwind.css";
import { logPageView } from "./utils/events.server";
import { getLoggedInUser } from "./services/auth.server";

export const links = () => [
  {
    rel: 'icon',
    type: 'image/png',
    href: '/favicon.png'
  },
  {
    rel: 'preconnect',
    href: 'https://fonts.googleapis.com'
  },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous'
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap'
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap'
  }
];

export async function loader({ request }) {
  // Log page view only for document requests (not fetches) so we get one event per page view
  const accept = request.headers.get("Accept") || "";
  if (accept.includes("text/html")) {
    const url = new URL(request.url);
    const user = await getLoggedInUser(request);
    logPageView(request, url.pathname, user ? { id: user.id } : null);
  }

  return json({
    ENV: {
      NODE_ENV: process.env.NODE_ENV,
      SENTRY_DSN: process.env.SENTRY_DSN,
    },
  });
}

export function Layout({ children }) {
  const data = useLoaderData();
  
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://app.healthq.care" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        {/* Expose ENV to client for Sentry and other client-side config */}
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(data?.ENV || {})}`,
          }}
        />
        <Scripts />
      </body>
    </html>
  );
}

/**
 * Error Boundary - Catches React errors and reports to Sentry
 */
export function ErrorBoundary() {
  const error = useRouteError();
  
  // Report error to Sentry
  captureRemixErrorBoundaryError(error);
  
  // Handle different error types
  if (isRouteErrorResponse(error)) {
    return (
      <html lang="en">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>{error.status} - HealthQ</title>
          <Meta />
          <Links />
        </head>
        <body className="bg-warm-cream min-h-screen flex items-center justify-center">
          <div className="text-center p-8">
            <h1 className="text-6xl font-display text-terracotta mb-4">{error.status}</h1>
            <p className="text-xl text-stone-600 mb-6">{error.statusText || "Something went wrong"}</p>
            <a 
              href="/" 
              className="inline-block px-6 py-3 bg-sage-green text-white rounded-xl hover:bg-sage-green/90 transition-colors"
            >
              Go Home
            </a>
          </div>
          <Scripts />
        </body>
      </html>
    );
  }
  
  // Generic error display
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Error - HealthQ</title>
        <Meta />
        <Links />
      </head>
      <body className="bg-warm-cream min-h-screen flex items-center justify-center">
        <div className="text-center p-8 max-w-md">
          <h1 className="text-4xl font-display text-terracotta mb-4">Oops!</h1>
          <p className="text-lg text-stone-600 mb-6">
            Something unexpected happened. We've been notified and are looking into it.
          </p>
          {process.env.NODE_ENV === 'development' && error?.message && (
            <pre className="text-left text-xs bg-stone-100 p-4 rounded-lg mb-6 overflow-auto">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          )}
          <a 
            href="/" 
            className="inline-block px-6 py-3 bg-sage-green text-white rounded-xl hover:bg-sage-green/90 transition-colors"
          >
            Go Home
          </a>
        </div>
        <Scripts />
      </body>
    </html>
  );
}

function App() {
  return <Outlet />;
}

// Wrap with Sentry for automatic error boundary integration
export default withSentry(App);
